import amaLog from '../helpers/log';
import { resolveCountryCode } from '../config/domain';

const statsCollectorAdsEndpoint = `${process.env.ADS_STATS_COLLECTOR}/stat/create`;
const statsCollectorPerfEndpoint = `${process.env.ADS_STATS_COLLECTOR}/stat/perf`;
const approvedPerfStatsSites = ['sol','seher','motherhood','femina','recept','billedbladet','seiska', 'elsaElle'];

interface Stats {
  clientTsInMs: number;
  site: string;
  stats: any[];
}

interface PerfStats {
  clientTsInMs: number;
  site: string;
  stats: any[];
  device: string | null;
  userAgent: string;
}

const isPerfStat = (stat: any): boolean => {
  if (!stat || typeof stat !== 'object') return false;

  return (
    'bundle_eval_ms' in stat ||
    'acc_config_ms' in stat ||
    'first_slot_requested_ms' in stat ||
    'consent_missing_in_bid' in stat ||
    'user_agent' in stat
  );
};

const extractPerfMetadata = (perfStats: any[]): { device: string | null; userAgent: string } => {
  if (perfStats.length === 0) {
    return { device: 'desktop', userAgent: '' };
  }
  const firstStat = perfStats[0];
  return {
    device: firstStat.device || 'desktop',
    userAgent: firstStat.user_agent || '',
  };
};

const cleanPerfStat = (stat: any): any => {
  const { device, user_agent, ...cleanedStat } = stat;
  return cleanedStat;
};

const isPerfSiteApproved = (site: string): boolean => approvedPerfStatsSites.includes(site);

const getSiteFromDomain = (site: string): string => {
  if (site === 'femina') {
    const cc = resolveCountryCode();
    const supportedCC = ['dk', 'fi'];
    return supportedCC.includes(cc) ? `femina${cc}` : 'femina';
  } else if (site === 'residencemagazine') {
    return 'residence';
  }
  return site;
};

export function AdStats(site: string) {
  let _site = getSiteFromDomain(site);
  let _stats: any[] = [];

  const add = (stats: any[]): any[] => {
    if (Array.isArray(stats) && stats.length) {
      _stats = [..._stats, ...stats];
    }
    return _stats;
  };

  const payload = (stats: any[] = _stats): Stats => ({
    clientTsInMs: Date.now(),
    site: _site,
    stats: [...stats],
  });

  const perfPayload = (stats: any[] = _stats): PerfStats => {
    const { device, userAgent } = extractPerfMetadata(stats);
    return {
      clientTsInMs: Date.now(),
      site: _site,
      stats: stats.map(cleanPerfStat),
      device,
      userAgent,
    };
  };

  const toJSON = (stats: any[] = _stats): string | undefined => {
    try {
      const response = JSON.stringify(payload(stats));
      return response;
    } catch (e) { }
  };

  const perfToJSON = (stats: any[] = _stats): string | undefined => {
    try {
      const response = JSON.stringify(perfPayload(stats));
      return response;
    } catch (e) { }
  };

  const appendFromJSON = (payload: string): void => {
    const parsed = JSON.parse(payload);
    _stats = [..._stats, ...parsed.stats];
  };

  const postStats = (stats: any[], endpoint: string, isPerfEndpoint: boolean = false): void => {
    const body = isPerfEndpoint ? perfToJSON(stats) : toJSON(stats);

    if (!body) {
      amaLog('AdStats::push - unable to retrieve payload..');
      return;
    }

    fetch(endpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body,
    }).catch(() => {
      amaLog(`AdStats::push - Unable to push stats to ${endpoint}, re-adding.`);
      appendFromJSON(body);
    });
  };

  const push = (): void => {
    if (!_stats.length) return;

    const perfStats = _stats.filter((stat) => isPerfStat(stat));
    const regularStats = _stats.filter((stat) => !isPerfStat(stat));
    _stats = [];

    regularStats.length && postStats(regularStats, statsCollectorAdsEndpoint, false);
    if (perfStats.length && isPerfSiteApproved(_site)) {
      postStats(perfStats, statsCollectorPerfEndpoint, true);
    } else if (perfStats.length) {
      amaLog(`AdStats::push - Perf stats disabled for site ${_site}`);
    }
  };

  return {
    all: (): any[] => _stats,
    add,
    push,
    toJSON,
    payload,
  };
}
